/**
 * trackEvent.js - Frontend behavioural event capture (Slice A).
 *
 * Single helper used across the app to record a user action into
 * Supabase `user_events`. Fires-and-forgets via `navigator.sendBeacon`
 * when available (so it doesn't block navigation), fetch() otherwise.
 *
 * Usage:
 *   import { trackEvent } from '@/lib/trackEvent';
 *   trackEvent('search', { query, results });
 *
 *   // Anywhere a user is logged in, supply their id:
 *   trackEvent('listing_view', { listing_id }, { user });
 *
 * Anonymous events carry a per-tab session_id (sessionStorage, not
 * persistent) so events within a single browser session correlate
 * client-side. We deliberately do NOT carry that id across tabs or
 * devices (per product owner 2026-02-15) - cross-device stitching
 * happens via email match at login.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'bp_track_session';

function getSessionId() {
  if (typeof window === 'undefined') return null;
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      // RFC4122-ish - good enough for client-side grouping
      sid = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
      );
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

/**
 * Record a behavioural event. Never throws - swallows network errors.
 *
 * @param {string} event_type   one of the EVENT_TYPES on the backend
 * @param {object} [payload]    free-form context (search query, listing id, etc.)
 * @param {object} [ctx]
 * @param {object} [ctx.user]    if known, the Supabase auth user
 * @param {string} [ctx.email]   if known, captured email (for later stitching)
 */
export function trackEvent(event_type, payload = {}, ctx = {}) {
  if (!API_BASE) return;
  if (!event_type) return;

  const body = {
    event_type,
    user_id: ctx.user?.id || null,
    email: ctx.email || ctx.user?.email || null,
    session_id: getSessionId(),
    page_path: typeof window !== 'undefined' ? window.location.pathname : null,
    referrer_path: typeof document !== 'undefined' ? document.referrer || null : null,
    payload,
  };

  const url = `${API_BASE}/api/track/event`;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    // Fallback - fire-and-forget fetch (no await)
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch {
    /* swallow - analytics must never break the page */
  }
}

/**
 * On login/signup, claim any anonymous capture rows for this email so
 * the user's pre-signup history attaches to their new profile. This is
 * Slice B (email-stitch only, no cross-device session_id stitching).
 */
export async function claimAnonymousHistory(user) {
  if (!API_BASE || !user?.id || !user?.email) return;
  try {
    await fetch(`${API_BASE}/api/track/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: user.id, email: user.email }),
      keepalive: true,
    });
  } catch {
    /* swallow */
  }
}

export default trackEvent;
